import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Mic2, Radio } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCursorContext } from '../../hooks/useCursorContext';

interface INeonProduct {
  name: string;
  label: string;
  href: string;
  description: string;
  signal: string;
  icon: LucideIcon;
}

const neonProducts: INeonProduct[] = [
  {
    name: 'NeoWispr',
    label: 'Native Dictation',
    href: '/project/neowispr',
    description: 'Mac-Diktat mit Offline-STT, Live-Übersetzung, Cleanup und eigenen Begriffen.',
    signal: '⌥ Space',
    icon: Mic2,
  },
  {
    name: 'NeoVoice',
    label: 'Voice Layer',
    href: '/project/neovoice',
    description: 'Voice-Assistant-Schicht für schnelle Gespräche, Interruptions und lokale Audio-Flows.',
    signal: 'Low latency',
    icon: Radio,
  },
  {
    name: 'Neon Core',
    label: 'Agent OS',
    href: '/project/neon-core',
    description: 'Gateway, Memory, Skills und Mission Control für echte Agent-Runs statt Demo-Bots.',
    signal: 'Runtime truth',
    icon: Bot,
  },
];

export function NeonLab() {
  const { setCursorState } = useCursorContext();

  const handleHover = (hovering: boolean) => {
    setCursorState(hovering ? 'hovering' : 'default');
  };

  return (
    <section className="relative overflow-hidden border-y border-emerald-500/10 bg-emerald-950/[0.08] px-6 py-16 sm:px-8 md:px-12 lg:px-24">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[110px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.4fr] lg:items-end">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-300/70">
              Neon Product Lab
            </span>
            <h2 className="mt-5 text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              Eigene AI-Produkte.
              <span className="block text-white/35">Nicht nur Kundenarbeit.</span>
            </h2>
          </div>

          <p className="max-w-2xl text-base leading-8 text-white/50 md:text-lg">
            NK Design baut inzwischen auch Neon: native Mac-Apps, Agent-Runtime,
            Memory, Voice und Workflows, die lokal wirklich laufen.
          </p>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {neonProducts.map((product) => {
            const Icon = product.icon;

            return (
              <Link
                key={product.name}
                to={product.href}
                className="group rounded-3xl border border-white/10 bg-black/35 p-5 transition-colors hover:border-emerald-400/40 hover:bg-emerald-500/[0.06]"
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-400/10">
                    <Icon className="h-5 w-5 text-emerald-300" />
                  </div>
                  <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-white/35">
                    {product.signal}
                  </span>
                </div>

                <div className="mt-8">
                  <span className="text-xs uppercase tracking-[0.24em] text-emerald-300/60">
                    {product.label}
                  </span>
                  <h3 className="mt-2 text-2xl font-light text-white">{product.name}</h3>
                  <p className="mt-4 min-h-24 text-sm leading-7 text-white/45">
                    {product.description}
                  </p>
                </div>

                <div className="mt-6 flex items-center gap-2 text-sm text-emerald-300/80">
                  Case ansehen
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
